import {useEffect, useState} from 'react'
import {Dispatch} from 'redux'
import moment from 'moment'
import '../accept-invite.scss'
import {formatSecondTime, handleNotificationWithToastify} from '../../../../services/Functional'
import {
  buildOtpDeliveryPolicyBlockedMessage,
  isOtpDeliveryPolicyBlockedMessage,
  normalizeOtpPolicyBlockedUserMessage,
  parseOtpFailureBlockMinutesFromConfig,
} from '../../otpDeliveryMessages'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {RootState} from '../../../../../setup'
import {AxiosResponse} from 'axios'

interface ResendOTPProps {
  className?: string
  APICalled: (dispatch: any) => Promise<void | AxiosResponse<any, any> | Record<string, unknown>>
  handleBackButton?: () => void
  showBackBtn?: boolean
}

function normalizeResendPayload(raw: unknown): {
  success: boolean
  message?: string
  code?: number
} {
  if (raw == null || typeof raw !== 'object') {
    return {success: false}
  }
  const r = raw as Record<string, unknown> & {
    data?: {success?: boolean; message?: string; code?: number}
  }
  const isAxiosLike = r.data !== undefined && typeof (r as {status?: unknown}).status === 'number'
  const body = isAxiosLike ? r.data! : (raw as {success?: boolean; message?: string; code?: number})
  return {
    success: body?.success === true,
    message: typeof body?.message === 'string' ? body.message : undefined,
    code: typeof body?.code === 'number' ? body.code : undefined,
  }
}

export function ResendOTP({className, APICalled, handleBackButton, showBackBtn}: ResendOTPProps) {
  const dispatch: Dispatch<any> = useDispatch()
  let navigate = useNavigate()

  const otpFailureBlockMinutes = useSelector((state: RootState) => {
    const cfg = state.configuration.configurations as Record<string, string> | null | undefined
    return parseOtpFailureBlockMinutesFromConfig(cfg?.security_otp_failure_block_minutes)
  })

  const countdownDurationShort = 60
  const countdownDurationLong = 120
  const maxAttempts = 3

  const [remainingTime, setRemainingTime] = useState(() => {
    const storedRemainingTime = localStorage.getItem('otpRemainingTime')
    return storedRemainingTime ? parseInt(storedRemainingTime) : countdownDurationShort
  })

  const [resendCount, setResendCount] = useState(() => {
    const storedResendCount = localStorage.getItem('otpResendCount')
    return storedResendCount ? parseInt(storedResendCount) : 0
  })

  const storedMaxResendCount = localStorage.getItem('otpMaxResendCount')
  const maxResendCount = storedMaxResendCount ? parseInt(storedMaxResendCount) : maxAttempts

  const [resendBlockedByPolicy, setResendBlockedByPolicy] = useState(false)
  const [otpPolicyBlockMessage, setOtpPolicyBlockMessage] = useState('')

  let intervalId: NodeJS.Timeout | undefined

  useEffect(() => {
    if (remainingTime > 0) {
      intervalId = setInterval(() => {
        setRemainingTime((prevTime) => prevTime - 1)
      }, 1000)
    } else {
      clearInterval(intervalId!)
    }

    return () => {
      clearInterval(intervalId!)
    }
  }, [remainingTime])

  const onClickResendBtn = async () => {
    const raw: unknown = await APICalled(dispatch)
    if (raw == null) {
      return
    }

    const {success, message: failureMessage, code} = normalizeResendPayload(raw)

    if (!success) {
      if (code === 401) {
        navigate('/error/auth')
        return
      }
      const toastMessage =
        failureMessage !== undefined && isOtpDeliveryPolicyBlockedMessage(failureMessage)
          ? normalizeOtpPolicyBlockedUserMessage(failureMessage, otpFailureBlockMinutes)
          : failureMessage
      const toastPayload =
        toastMessage !== undefined
          ? {success: false, code, message: toastMessage}
          : {success: false, code}
      handleNotificationWithToastify(toastPayload)
      if (isOtpDeliveryPolicyBlockedMessage(failureMessage)) {
        setResendBlockedByPolicy(true)
        setOtpPolicyBlockMessage(
          normalizeOtpPolicyBlockedUserMessage(failureMessage, otpFailureBlockMinutes)
        )
        setRemainingTime(0)
      }
      return
    }

    const isAxios =
      (raw as {data?: unknown; status?: unknown})?.data !== undefined &&
      typeof (raw as {status?: unknown}).status === 'number'
    if (
      !isAxios ||
      ((raw as AxiosResponse).status !== 200 && (raw as AxiosResponse).status !== 201)
    ) {
      handleNotificationWithToastify((raw as AxiosResponse)?.data ?? {success: false})
      return
    }

    if (resendCount < maxResendCount) {
      setResendCount((prevCount) => {
        const newCount = prevCount + 1
        localStorage.setItem('otpResendCount', newCount.toString())
        localStorage.setItem('otpResendTime', moment().valueOf().toString())
        localStorage.setItem('otpRemainingTime', countdownDurationShort.toString())
        return newCount
      })
      setRemainingTime(countdownDurationShort)
    } else {
      setResendCount(() => {
        const newCount = 0
        localStorage.setItem('otpResendCount', newCount.toString())
        localStorage.setItem('otpResendTime', moment().valueOf().toString())
        localStorage.setItem('otpRemainingTime', countdownDurationShort.toString())
        return newCount
      })
      setRemainingTime(countdownDurationLong)
    }
  }

  useEffect(() => {
    localStorage.setItem('otpRemainingTime', remainingTime.toString())
    localStorage.setItem('otpResendCount', resendCount.toString())
    localStorage.setItem('otpMaxResendCount', maxResendCount.toString())
  }, [remainingTime, resendCount, maxResendCount])

  return (
    <div>
      <div className={`d-flex justify-content-between ${showBackBtn && 'otp-btn-align'}`}>
        {showBackBtn && (
          <button
            className={`btn btn-link p-0 d-flex align-items-center resend-btn ${className} `}
            onClick={handleBackButton}
          >
            <i className='bi bi-chevron-left text-primary resend-back-button'></i>
            <u>Back to login</u>
          </button>
        )}
        {resendBlockedByPolicy ? (
          <button
            type='button'
            className={`${className} btn btn-link p-0 d-flex resend-btn mx-4`}
            disabled
            aria-disabled
          >
            <u>Resend Code</u>
          </button>
        ) : remainingTime !== 0 ? (
          <div className={`${className} resend-text mx-4`}>
            Resend Code in <span className='seconds'>00:{formatSecondTime(remainingTime)}</span>
          </div>
        ) : (
          <button
            className={`${className} btn btn-link p-0 d-flex resend-btn mx-4`}
            onClick={onClickResendBtn}
            disabled={remainingTime !== 0}
          >
            <u>Resend Code</u>
          </button>
        )}
      </div>
      {resendBlockedByPolicy ? (
        <p className='text-danger small mt-2 mb-0 mx-4' role='alert'>
          {otpPolicyBlockMessage || buildOtpDeliveryPolicyBlockedMessage(otpFailureBlockMinutes)}
        </p>
      ) : null}
    </div>
  )
}
